import { useContext, useEffect, useState } from 'react'
import * as React from 'react'
import {
  Fieldset,
  If,
  Legend,
  Loader,
} from '@paytrace/pt-skel/Component'
import { AppCtx } from '@paytrace/pt-skel/Context'
import PubSub from '@paytrace/pt-skel/Event/PubSub'
import Event from '../Event'
import { BatchTransactionOpResult } from '../types'
import { ProcessButton } from '../Component'
import { formatCurrency, sanitizeNumber } from '../Helper/util'
import { getLegacyDomain } from '@paytrace/pt-skel/Helper'

interface Props extends React.ComponentProps<any> {
  action: string
  onSubmit: (e: React.FormEvent<HTMLFormElement>) => any
}

export function renderAmountHeader (action: string) {
  switch (action.toLowerCase()) {
    case 'refund':
      return 'Refund Amount'
    case 'void':
    case 'capture':
    default:
      return 'Amount'
  }
}

const getManualOrDefaultAmount = (manualAmounts: { [key: string]: string }, idx: string, defaultAmount: number) => {
  const manualAmount = manualAmounts[idx]
  // If user remove the input value
  if (manualAmount === '') {
    return '0'
  }
  return manualAmount !== undefined ? manualAmount : defaultAmount.toFixed(2)
}

export function renderAmount (
  action: string,
  row: { transaction_id: string, settle_amount: number, max_refundable_amount: number, surcharge_was_applied: boolean },
  onChange: any,
  manualAmounts: { [key: string]: string },
) {
  const idx = row.transaction_id
  const allow_partial = row.surcharge_was_applied
  const amountDefault = action.toLowerCase() === 'refund'
    ? row.max_refundable_amount
    : row.settle_amount

  const amount = '$' + getManualOrDefaultAmount(manualAmounts, idx, amountDefault)

  switch (action.toLowerCase()) {
    case 'refund':
      return (<input type='text' value={amount} onChange={onChange} />)

    case 'capture':
      return (<input disabled={allow_partial} type='text' value={amount} onChange={allow_partial || onChange} />)

    case 'void':
    default:
      return (<input disabled type='text' value={amount} onChange={onChange} />)
  }
}

function getSurchargeAmount (
  row: { transaction_id: string, settle_amount: number, max_refundable_amount: number, surcharge_was_applied: boolean, surcharge_amount: number, surcharge_rate: number },
  manualAmounts: { [key: string]: string }
) {
  const idx = row.transaction_id

  if (!row.surcharge_was_applied) {
    return 0
  }

  const amount = parseFloat(getManualOrDefaultAmount(manualAmounts, idx, row.max_refundable_amount))

  if (row.max_refundable_amount === row.settle_amount && row.max_refundable_amount === amount) {
    return row.surcharge_amount
  }
  // Calculate surcharge based on whether the max refundable amount is different
  const surchargeAmount = (amount * row.surcharge_rate) / 100
  return surchargeAmount
}

export function renderSurchargeAmount (
  row: { transaction_id: string, settle_amount: number, max_refundable_amount: number, surcharge_was_applied: boolean, surcharge_amount: number, surcharge_rate: number },
  manualAmounts: { [key: string]: string }
): string {
  const surchargeAmount = getSurchargeAmount(row, manualAmounts)
  return formatCurrency(surchargeAmount)
}

export function renderTotalAmount (
  row: { transaction_id: string, settle_amount: number, max_refundable_amount: number, surcharge_was_applied: boolean, surcharge_amount: number, surcharge_rate: number },
  manualAmounts: { [key: string]: string }
): string {
  const idx = row.transaction_id
  const manualAmount = parseFloat(getManualOrDefaultAmount(manualAmounts, idx, row.max_refundable_amount))
  const surchargeAmount = getSurchargeAmount(row, manualAmounts)
  const totalAmount = manualAmount + surchargeAmount
  return formatCurrency(totalAmount)
}

export function TransactionSearchResult (props: Props) {
  const context = useContext(AppCtx)
  const merchant_id = context.merchant
  const [selectedIds, setSelectedIds] = useState<string[]>([])
  const [manualAmounts, setManualAmounts] = useState<{ [key: string]: string }>({})
  const [hideRows, setHideRows] = useState(false)
  const [isLoading, setIsLoading] = useState(false)
  const [hasSurcharge, setHasSurcharge] = useState(false)

  useEffect(() => {
    if (props.action !== 'refund') {
      return
    }
    const surchargeApplied = props.rows.some((row: { surcharge_was_applied: boolean }) => row.surcharge_was_applied)
    setHasSurcharge(surchargeApplied)

    return () => {
      PubSub.unsub(self)
    }
  }, [props.rows, props.action])

  useEffect(() => {
    PubSub.sub(self, Event.BATCH_TRANSACTIONS_OP_RESPONSE, (rawJson: any) => {
      window.scrollTo(0, 0)
      setIsLoading(false)
      setSelectedIds([])

      // Some unexpected error was hit, ugh - this should never be the case, so
      // dump as much info as we can.
      if ('err' in rawJson) {
        const msg = 'An unexpected error was encountered, full info: ' + JSON.stringify(rawJson.err)
        PubSub.pub(Event.NETWORK_ERROR, msg)
        setHideRows(false)

        return
      }

      setHideRows(true)
      // If we got here, we got the expected type of response
      const json: BatchTransactionOpResult[] = rawJson
      const good: BatchTransactionOpResult[] = []
      const bad: BatchTransactionOpResult[] = []

      // For the response_codes:
      // http://localhost:8100/redoc#tag/Response-Codes
      // 106 = successful refund
      // 107 = unsuccessful refund
      // 108 = successful test refund
      // 109 = successful void
      // 110 = unsuccessful void
      // 112 = successful capture
      // 113 = unsuccessful capture
      // Might be safer to just look at the approval_message which should be APPROVAL
      const successCodes = [
        106,
        108,
        109,
        112,
      ]

      const successStatuses = [200, 201] // TODO: Make TSYS MS send a 200 back

      json.forEach((response: BatchTransactionOpResult) => {
        let responseCode = 0

        if (Array.isArray(response.data)) {
          responseCode = response.data[0].response_code
        } else {
          responseCode = response.data.response_code
        }

        if (successStatuses.includes(response.status_code) &&
            successCodes.includes(responseCode)) {
          good.push(response)
        } else {
          bad.push(response)
        }
      })

      const records: { [key: string]: any } = {}

      props.rows.forEach((row: {
        requested_amount: number,
        transaction_id: string }) => {
        const idx = String(row.transaction_id)

        // Ensure our requested_amount reflects the user specified value
        // This is used for feedback in BatchTransactionFeedback only
        if (manualAmounts[idx] !== undefined) {
          row.requested_amount = Number(manualAmounts[idx])
        }

        records[idx] = row
      })

      PubSub.pub(Event.BATCH_TRANSACTION_ERROR_FEEDBACK, { response: bad, records })
      PubSub.pub(Event.BATCH_TRANSACTION_FEEDBACK, { response: good, records })
    })

    return () => {
      PubSub.unsub(self)
    }
  }, [props.rows, manualAmounts])

  function handleSelect (selectedId: string) {
    return (e: any) => {
      if (e.target.checked) {
        setSelectedIds([...selectedIds, selectedId])
      } else {
        setSelectedIds(selectedIds.filter(id => id !== selectedId))
      }
    }
  }

  function handleManualAmount (
    row: { transaction_id: string }
  ) {
    return (e: any) => {
      setManualAmounts({ ...manualAmounts, [row.transaction_id]: sanitizeNumber(e.target.value) })
    }
  }

  function renderTable (rows: any[]) {
    const domain = getLegacyDomain() + '/view.pay?b='
    const view_link = (id: string) => domain + id

    return (
      <table>
        <thead>
          <tr>
            <th>&nbsp;</th>
            <th>Order ID</th>
            <th>Card Number</th>
            <th>{renderAmountHeader(props.action)}</th>
            {hasSurcharge &&
            <>
              <th>Surcharge Refund</th>
              <th>Total Refund</th>
            </>
            }
            <th>Date/Time</th>
            <th>Invoice</th>
            <th>Name</th>
            <th>AVS</th>
            <th>CSC</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((row, iter) => {
            // So much for a one size fit's all solution.  This page is used
            // for void/refund/auth list items, however they each have a special
            // behavior.
            //   Refunds: Using max_refundable_amount, there is a refund column, and
            //     this should set the 'amount' value alongside each record in the /batch
            //     sent data.  This also may need 2 more surcharge refund columns when
            //     we incorporate that work.
            //   Voids: Well, no special behavior
            //   Capture: The 'amount' should be user supplied, but I assume, not exceed
            //     requested_amount (I am not sure if this is enforced).  This is to
            //     support partial capture.  I don't think a partial capture can capture
            //     multiple times.

            // TODO: Also need the customer id for linking to something like:
            // https://vt.devqa.paytrace.com/mcust.pay?a=mcust&cid=11268074
            return (
              <tr key={iter}>
                <td>
                  <input
                    onChange={handleSelect(row.transaction_id)}
                    type='checkbox' />
                </td>
                <td>
                  <a href={view_link(row.transaction_id)}
                     rel='noreferrer'
                     target='_blank'>
                    {row.transaction_id}
                  </a>
                </td>
                <td>{row.card_masked}</td>
                <td>
                  {renderAmount(
                    props.action,
                    row,
                    handleManualAmount(row),
                    manualAmounts)}
                </td>
                {hasSurcharge &&
                <>
                  <td>{renderSurchargeAmount(row, manualAmounts)}</td>
                  <td>{renderTotalAmount(row, manualAmounts)}</td>
                </>
                }
                <td>{new Date(row.created_at).toLocaleString()}</td>
                <td>{row.invoice_number}</td>
                <td>{row.billing_name}</td>
                <td>&nbsp;</td>
                <td>&nbsp;</td>
              </tr>
            )
          })}
        </tbody>
      </table>
    )
  }

  function doSubmit (e: any, rows: any[]) {
    e.preventDefault()
    const { action } = props

    const confirmed = confirm(
      `Are you sure you want to ${action} the selected transaction(s)?`
    )

    if (!confirmed) {
      return
    }

    const batch_items = selectedIds.map((id: string) => {
      const transaction: { transaction_id: string, amount?: number } = {
        transaction_id: id,
      }

      // User set the value manually from the default - omitting
      // this ensures API uses the default itself
      if (manualAmounts[id] !== undefined &&
          Number(manualAmounts[id]) !== Number(transaction.amount)) {
        transaction.amount = Number(manualAmounts[id])
      }

      return transaction
    })
    const payload = {
      merchant_id: Number(merchant_id),
      batch_items,
      action,
    }

    // Wipe out previous feedback messages, as we want the fresh loaded ones.
    PubSub.pub(Event.BATCH_TRANSACTION_ERROR_FEEDBACK, { response: [], records: {} })
    PubSub.pub(Event.BATCH_TRANSACTION_FEEDBACK, { response: [], records: {} })
    PubSub.pub(Event.BATCH_TRANSACTIONS_OP_REQUEST, payload)
    window.scrollTo(0, 0)
    setHideRows(true)
    setIsLoading(true)
  }

  if (isLoading) {
    return (
      <Loader />
    )
  }

  if (hideRows) {
    return (
      <React.Fragment />
    )
  }

  return (
    <form onSubmit={(e) => doSubmit(e, props.rows)}>
      <Fieldset>
        <Legend title='Search Results'></Legend>
        <div className='clear' />

        <If is={props.rows.length > 0}>
          {renderTable(props.rows)}
        </If>

        <div className='info-msg msg-space'>
          {props.rows.length} transactions were found in this date range.
        </div>
      </Fieldset>
      <ProcessButton records={context.records} required_permissions={[props.action]} />
    </form>
  )
}

export default TransactionSearchResult
